<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import IntegrationEventsAPI from 'dashboard/api/integrationEvents';

const store = useStore();
const { t, locale } = useI18n();
// Locale BCP-47 para Intl (o i18n usa '_' tipo pt_BR; Intl quer '-').
const dateLocale = computed(() => (locale.value || 'pt_BR').replace('_', '-'));
function formatEventDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(dateLocale.value);
}
const accountId = computed(() => store.getters.getCurrentAccountId);

const events = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(50);
const loading = ref(false);
const filters = ref({ provider: '', status: '', funnel_id: '', from: '', to: '' });
// Inputs visuais em dd/mm/yyyy. Sao convertidos pra ISO antes de mandar
// pro backend (filters.from/to mantem ISO interno).
const dateFrom = ref('');
const dateTo = ref('');

// Parser tolerante: aceita dd/mm/yyyy, dd-mm-yyyy ou yyyy-mm-dd
function parseDate(input) {
  if (!input) return '';
  const s = String(input).trim();
  // ISO ja
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return s;
  // dd/mm/yyyy ou dd-mm-yyyy
  m = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return ''; // invalido — ignora
}

function clearDates() {
  dateFrom.value = '';
  dateTo.value = '';
  filters.value.from = '';
  filters.value.to = '';
  applyFilters();
}
const summary = ref({});
const funnels = computed(() => store.getters['funnel/getFunnels'] || []);

async function load() {
  loading.value = true;
  try {
    const params = { page: page.value, per_page: perPage.value };
    Object.entries(filters.value).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await IntegrationEventsAPI.list(params);
    events.value = data.data || [];
    total.value = data.meta?.total || 0;
  } catch (e) {
    useAlert(t('REPORTS.INTEGRATION_EVENTS.LOAD_ERROR'));
  } finally { loading.value = false; }
}

async function loadSummary() {
  try {
    const params = filters.value.funnel_id ? { funnel_id: filters.value.funnel_id } : {};
    const { data } = await IntegrationEventsAPI.summary(params);
    summary.value = data;
  } catch (e) { /* silencioso */ }
}

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)));
const providerLabel = computed(() => ({ google_ads: t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS'), meta_capi: t('REPORTS.INTEGRATION_EVENTS.META_ADS') }));
const statusLabel = computed(() => ({ success: t('REPORTS.INTEGRATION_EVENTS.STATUS_OK'), error: t('REPORTS.INTEGRATION_EVENTS.STATUS_ERROR'), partial_failure: t('REPORTS.INTEGRATION_EVENTS.STATUS_PARTIAL'), skipped: t('REPORTS.INTEGRATION_EVENTS.STATUS_SKIPPED') }));
const statusBadge = {
  success: 'bg-emerald-500',
  error: 'bg-ruby-9',
  partial_failure: 'bg-amber-500',
  skipped: 'bg-n-slate-9',
};

function applyFilters() {
  filters.value.from = parseDate(dateFrom.value);
  filters.value.to   = parseDate(dateTo.value);
  page.value = 1;
  load();
  loadSummary();
}
function nextPage() { if (page.value < totalPages.value) { page.value++; load(); } }
function prevPage() { if (page.value > 1) { page.value--; load(); } }

watch(() => filters.value.funnel_id, () => loadSummary());

onMounted(() => { store.dispatch('funnel/get'); load(); loadSummary(); });
</script>

<template>
  <div class="p-6 max-w-screen-xl mx-auto">
    <header class="mb-4">
      <h2 class="text-lg font-extrabold text-n-slate-12 mb-1">{{ $t('REPORTS.INTEGRATION_EVENTS.TITLE') }}</h2>
      <p class="text-sm text-n-slate-11">{{ $t('REPORTS.INTEGRATION_EVENTS.SUBTITLE') }}</p>
    </header>

    <div v-if="summary && (summary.google_ads || summary.meta_capi)" class="flex gap-4 mb-5 flex-wrap">
      <div class="bg-n-slate-2 rounded-md p-3 min-w-[200px]">
        <h4 class="text-xs font-semibold text-n-slate-12 mb-1">{{ $t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS') }}</h4>
        <div class="flex gap-3 text-xs">
          <span class="text-emerald-600 font-semibold">{{ summary.google_ads?.success || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.OK_SHORT') }}</span>
          <span class="text-amber-600 font-semibold">{{ summary.google_ads?.partial_failure || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.PARTIAL_SHORT') }}</span>
          <span class="text-ruby-9 font-semibold">{{ summary.google_ads?.error || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.ERROR_SHORT') }}</span>
        </div>
      </div>
      <div class="bg-n-slate-2 rounded-md p-3 min-w-[200px]">
        <h4 class="text-xs font-semibold text-n-slate-12 mb-1">{{ $t('REPORTS.INTEGRATION_EVENTS.META_ADS') }}</h4>
        <div class="flex gap-3 text-xs">
          <span class="text-emerald-600 font-semibold">{{ summary.meta_capi?.success || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.OK_SHORT') }}</span>
          <span class="text-amber-600 font-semibold">{{ summary.meta_capi?.partial_failure || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.PARTIAL_SHORT') }}</span>
          <span class="text-ruby-9 font-semibold">{{ summary.meta_capi?.error || 0 }} {{ $t('REPORTS.INTEGRATION_EVENTS.ERROR_SHORT') }}</span>
        </div>
      </div>
    </div>

    <div class="flex gap-2 flex-wrap mb-4">
      <select v-model="filters.provider" @change="applyFilters" class="px-3 py-1.5 rounded border border-n-slate-4 bg-n-background text-n-slate-12 text-xs">
        <option value="">{{ $t('REPORTS.INTEGRATION_EVENTS.ALL_PROVIDERS') }}</option>
        <option value="google_ads">{{ $t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS') }}</option>
        <option value="meta_capi">{{ $t('REPORTS.INTEGRATION_EVENTS.META_ADS') }}</option>
      </select>
      <select v-model="filters.status" @change="applyFilters" class="px-3 py-1.5 rounded border border-n-slate-4 bg-n-background text-n-slate-12 text-xs">
        <option value="">{{ $t('REPORTS.INTEGRATION_EVENTS.ALL_STATUSES') }}</option>
        <option value="success">{{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_OK') }}</option>
        <option value="partial_failure">{{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_PARTIAL') }}</option>
        <option value="error">{{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_ERROR') }}</option>
      </select>
      <select v-model="filters.funnel_id" @change="applyFilters" class="px-3 py-1.5 rounded border border-n-slate-4 bg-n-background text-n-slate-12 text-xs">
        <option value="">{{ $t('REPORTS.INTEGRATION_EVENTS.ALL_FUNNELS') }}</option>
        <option v-for="f in funnels" :key="f.id" :value="f.id">{{ f.name }}</option>
      </select>
      <input type="text" v-model="dateFrom" @blur="applyFilters" maxlength="10" placeholder="dd/mm/yyyy"
        class="px-3 py-1.5 rounded border border-n-slate-4 bg-n-background text-n-slate-12 text-xs w-28" />
      <span class="text-n-slate-11 text-xs self-center">{{ $t('REPORTS.INTEGRATION_EVENTS.DATE_TO') }}</span>
      <input type="text" v-model="dateTo" @blur="applyFilters" maxlength="10" placeholder="dd/mm/yyyy"
        class="px-3 py-1.5 rounded border border-n-slate-4 bg-n-background text-n-slate-12 text-xs w-28" />
      <button v-if="dateFrom || dateTo" class="text-xs px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12" @click="clearDates">{{ $t('REPORTS.INTEGRATION_EVENTS.CLEAR') }}</button>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">{{ $t('REPORTS.INTEGRATION_EVENTS.LOADING') }}</div>

    <div v-else-if="events.length" class="overflow-x-auto rounded-xl border border-n-slate-4">
      <table class="w-full text-xs">
        <thead class="bg-n-slate-2 text-n-slate-11">
          <tr>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_WHEN') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_PROVIDER') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_STATUS') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_CARD') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_STAGE') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_HTTP') }}</th>
            <th class="px-3 py-2 text-left font-semibold">{{ $t('REPORTS.INTEGRATION_EVENTS.COL_DETAIL') }}</th>
          </tr>
        </thead>
        <tbody class="text-n-slate-12">
          <tr v-for="(e, idx) in events" :key="idx" class="border-t border-n-slate-4">
            <td class="px-3 py-2 align-top">{{ formatEventDate(e.occurred_at) }}</td>
            <td class="px-3 py-2 align-top">{{ providerLabel[e.provider] || e.provider }}</td>
            <td class="px-3 py-2 align-top">
              <span :class="['px-2 py-0.5 rounded text-white text-[10px] font-semibold', statusBadge[e.status] || 'bg-n-slate-9']">
                {{ statusLabel[e.status] || e.status }}
              </span>
            </td>
            <td class="px-3 py-2 align-top">{{ e.item_title || ('#' + e.kanban_item_id) }}</td>
            <td class="px-3 py-2 align-top">{{ e.event_label || e.stage_key }}</td>
            <td class="px-3 py-2 align-top">{{ e.http_status || '-' }}</td>
            <td class="px-3 py-2 align-top text-n-slate-11 font-mono text-[10px] max-w-[280px] break-all">{{ e.error ? String(e.error).slice(0, 100) : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="p-12 text-center bg-n-slate-2 rounded-md text-n-slate-11 text-sm">{{ $t('REPORTS.INTEGRATION_EVENTS.EMPTY') }}</div>

    <footer class="mt-4 flex justify-between items-center text-xs text-n-slate-11">
      <button class="px-3 py-1 rounded border border-n-slate-4 hover:bg-n-slate-3 disabled:opacity-40" :disabled="page === 1" @click="prevPage">&larr; {{ $t('REPORTS.INTEGRATION_EVENTS.PREVIOUS') }}</button>
      <span>{{ $t('REPORTS.INTEGRATION_EVENTS.PAGINATION', { page, totalPages, total }) }}</span>
      <button class="px-3 py-1 rounded border border-n-slate-4 hover:bg-n-slate-3 disabled:opacity-40" :disabled="page >= totalPages" @click="nextPage">{{ $t('REPORTS.INTEGRATION_EVENTS.NEXT') }} &rarr;</button>
    </footer>
  </div>
</template>
